import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Gauge,
  Scale,
  Droplets,
  Banknote,
  Anchor,
  CalendarClock,
  TrendingUp,
  Percent,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Plus,
  Trash2,
  Save,
  Calculator,
  Landmark,
  Info,
  BookOpen,
  Pencil,
  FileSpreadsheet,
} from "lucide-react";
import { GelirTablosuSekmesi } from "../components/GelirTablosuSekmesi";
import { AylikOzetRaporu } from "../components/AylikOzetRaporu";
import { MizanSekmesi } from "../components/MizanSekmesi";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const DURUM_STIL = {
  iyi: { etiket: "İyi", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50" },
  orta: { etiket: "Orta", cls: "bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-50" },
  zayif: { etiket: "Zayıf", cls: "bg-red-50 text-red-700 border border-red-200 hover:bg-red-50" },
};

const oranFmt = (v, tip) => {
  if (v === null || v === undefined) return "—";
  if (tip === "yuzde") return `%${v.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`;
  if (tip === "gun") return `${v.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} gün`;
  return v.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
};

export default function FinansalAnalizPage() {
  const { user } = useAuth();
  const yonetici = user.role === "yonetici";
  const [tab, setTab] = useState("oranlar");
  const yil = 2026;

  // --- Oran paneli ---
  const [analiz, setAnaliz] = useState(null);
  const [analizLoading, setAnalizLoading] = useState(true);
  const [seciliAy, setSeciliAy] = useState("son"); // "son" | "1".."12"

  const fetchAnaliz = useCallback(async () => {
    setAnalizLoading(true);
    try {
      const params = { yil };
      if (seciliAy !== "son") params.ay = parseInt(seciliAy);
      const res = await api.get("/finansal-analiz", { params });
      setAnaliz(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Finansal analiz verileri yüklenemedi."));
    } finally {
      setAnalizLoading(false);
    }
  }, [seciliAy]);

  // --- Bilanço ---
  const [bilancolar, setBilancolar] = useState([]);
  const fetchBilancolar = useCallback(async () => {
    try {
      const res = await api.get("/bilanco");
      setBilancolar(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Bilanço kayıtları yüklenemedi."));
    }
  }, []);

  // --- Yatırım senaryoları ---
  const [senaryolar, setSenaryolar] = useState([]);
  const fetchSenaryolar = useCallback(async () => {
    try {
      const res = await api.get("/yatirim-senaryolari");
      setSenaryolar(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Senaryolar yüklenemedi."));
    }
  }, []);

  useEffect(() => {
    fetchAnaliz();
  }, [fetchAnaliz]);

  useEffect(() => {
    fetchBilancolar();
    fetchSenaryolar();
  }, [fetchBilancolar, fetchSenaryolar]);

  const veriYenile = () => {
    fetchAnaliz();
    fetchBilancolar();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-4" data-testid="finansal-analiz-page">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full flex-wrap h-auto gap-1 justify-start" data-testid="finansal-analiz-tabs">
          <TabsTrigger value="oranlar" className="gap-1.5 min-h-[40px]" data-testid="tab-oran-paneli">
            <Gauge className="h-4 w-4" /> Oran Paneli
          </TabsTrigger>
          <TabsTrigger value="bilanco" className="gap-1.5 min-h-[40px]" data-testid="tab-bilanco-girisi">
            <Landmark className="h-4 w-4" /> Aylık Bilanço Girişi
          </TabsTrigger>
          <TabsTrigger value="gelir-tablosu" className="gap-1.5 min-h-[40px]" data-testid="tab-gelir-tablosu">
            <FileSpreadsheet className="h-4 w-4" /> Gelir Tablosu
          </TabsTrigger>
          <TabsTrigger value="aylik-ozet" className="gap-1.5 min-h-[40px]" data-testid="tab-aylik-ozet">
            Aylık Özet
          </TabsTrigger>
          <TabsTrigger value="mizan" className="gap-1.5 min-h-[40px]" data-testid="tab-mizan">
            Mizan
          </TabsTrigger>
          <TabsTrigger value="yatirim" className="gap-1.5 min-h-[40px]" data-testid="tab-yatirim-araci">
            <Calculator className="h-4 w-4" /> Yatırım Karar Aracı
          </TabsTrigger>
          <TabsTrigger value="kavramlar" className="gap-1.5 min-h-[40px]" data-testid="tab-kavramlar">
            <BookOpen className="h-4 w-4" /> Finansal Kavramlar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="oranlar" className="mt-4">
          <OranPaneli
            analiz={analiz}
            loading={analizLoading}
            seciliAy={seciliAy}
            setSeciliAy={setSeciliAy}
            bilancoyaGit={() => setTab("bilanco")}
          />
        </TabsContent>

        <TabsContent value="bilanco" className="mt-4">
          <BilancoGirisi yonetici={yonetici} bilancolar={bilancolar} onKaydet={veriYenile} yil={yil} />
        </TabsContent>

        <TabsContent value="gelir-tablosu" className="mt-4">
          <GelirTablosuSekmesi yonetici={yonetici} yil={yil} />
        </TabsContent>

        <TabsContent value="aylik-ozet" className="mt-4">
          <AylikOzetRaporu yil={yil} />
        </TabsContent>

        <TabsContent value="mizan" className="mt-4">
          <MizanSekmesi yonetici={yonetici} yil={yil} />
        </TabsContent>

        <TabsContent value="yatirim" className="mt-4">
          <YatirimAraci yonetici={yonetici} senaryolar={senaryolar} onDegisim={fetchSenaryolar} />
        </TabsContent>

        <TabsContent value="kavramlar" className="mt-4">
          <FinansalKavramlar />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}

/* ==================== SEKME 1: ORAN PANELİ ==================== */
function OranPaneli({ analiz, loading, seciliAy, setSeciliAy, bilancoyaGit }) {
  if (loading && !analiz) {
    return (
      <div className="space-y-4" data-testid="oran-paneli-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (!analiz) return null;

  if (!analiz.bilanco_var) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center" data-testid="oran-paneli-bos">
        <Landmark className="mx-auto h-10 w-10 text-muted-foreground/50" />
        <h3 className="mt-3 font-display text-base font-semibold">Henüz bilanço verisi yok</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Finansal oranların hesaplanabilmesi için önce aylık bilanço verilerini girmeniz gerekir.
        </p>
        <Button onClick={bilancoyaGit} className="mt-4 min-h-[44px]" data-testid="oran-paneli-bilanco-git-button">
          <Plus className="mr-1.5 h-4 w-4" /> Bilanço Girişine Git
        </Button>
      </div>
    );
  }

  const o = analiz.oranlar;
  const h = analiz.hesap;
  const kartlar = [
    { key: "cari_oran", label: "Cari Oran", icon: Scale, tip: "oran", formul: "Dönen Varlıklar ÷ KV Borçlar", hedef: "Hedef: ≥ 1,5" },
    { key: "asit_test", label: "Asit-Test Oranı", icon: Droplets, tip: "oran", formul: "(Dönen Varlıklar − Stok) ÷ KV Borçlar", hedef: "Hedef: ≥ 1,0" },
    { key: "nakit_oran", label: "Nakit Oran", icon: Banknote, tip: "oran", formul: "Kasa & Banka ÷ KV Borçlar", hedef: "Hedef: ≥ 0,2" },
    { key: "kaldirac", label: "Kaldıraç Oranı", icon: Anchor, tip: "oran", formul: "Toplam Borç ÷ Toplam Varlık", hedef: "Hedef: ≤ 0,5" },
    { key: "dso", label: "DSO (Alacak Tahsil Süresi)", icon: CalendarClock, tip: "gun", formul: "(Alacaklar ÷ Ciro) × Gün", hedef: "Hedef: ≤ 45 gün" },
    { key: "roe", label: "ROE (Özkaynak Kârlılığı)", icon: TrendingUp, tip: "yuzde", formul: "Yıllık Net Kâr ÷ Özkaynak", hedef: "Hedef: ≥ %20" },
    { key: "roa", label: "ROA (Varlık Kârlılığı)", icon: Percent, tip: "yuzde", formul: "Yıllık Net Kâr ÷ Toplam Varlık", hedef: "Hedef: ≥ %10" },
  ];

  return (
    <div className="space-y-4">
      {/* Dönem seçici */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Dönem:</Label>
          <Select value={seciliAy} onValueChange={setSeciliAy}>
            <SelectTrigger className="w-[180px] min-h-[40px] bg-card" data-testid="oran-paneli-donem-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="son" data-testid="oran-donem-son">Son Dönem</SelectItem>
              {AYLAR.map((a, i) => (
                <SelectItem key={a} value={String(i + 1)} data-testid={`oran-donem-${i + 1}`}>
                  {a} 2026 {analiz.mevcut_aylar.includes(i + 1) ? "" : "(veri yok)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Badge className="bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/15 hover:bg-[hsl(var(--primary))]/5" data-testid="oran-paneli-kullanilan-donem">
          Gösterilen: {AYLAR[analiz.kullanilan_ay - 1]} 2026
        </Badge>
      </div>

      {/* Notlar */}
      {analiz.notlar.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/70 px-3 py-2.5 space-y-1" data-testid="oran-paneli-notlar">
          {analiz.notlar.map((n, i) => (
            <p key={`${i}-${n}`} className="flex items-start gap-1.5 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {n}
            </p>
          ))}
        </div>
      )}

      {/* Oran kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="oran-paneli-kartlar">
        {kartlar.map((k) => {
          const veri = o[k.key];
          const durum = veri.durum ? DURUM_STIL[veri.durum] : null;
          return (
            <div key={k.key} className="rounded-xl border bg-card p-4 shadow-[0_1px_0_rgba(14,27,46,0.06),0_8px_24px_rgba(14,27,46,0.06)]" data-testid={`oran-kart-${k.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-muted-foreground min-w-0">
                  <k.icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide truncate">{k.label}</span>
                </div>
                {durum && (
                  <Badge className={`${durum.cls} text-[10px] px-1.5 py-0 shrink-0`} data-testid={`oran-durum-${k.key}`}>
                    {durum.etiket}
                  </Badge>
                )}
              </div>
              <p className="mt-2 font-display text-2xl font-bold tabular-nums" data-testid={`oran-deger-${k.key}`}>
                {oranFmt(veri.deger, k.tip)}
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground leading-relaxed">{k.formul}</p>
              <p className="text-[10px] font-medium text-muted-foreground/80">{k.hedef}</p>
            </div>
          );
        })}
      </div>

      {/* Bilanço özeti */}
      <div className="rounded-xl border bg-card p-4" data-testid="oran-paneli-bilanco-ozeti">
        <h3 className="font-display text-sm font-semibold mb-1">Bilanço Özeti — {AYLAR[analiz.kullanilan_ay - 1]} 2026</h3>
        <p className="text-[11px] text-muted-foreground mb-3">
          Ticari alacaklar ({formatTL(h.alacaklar)}) Ciro modülündeki açık kayıtlardan otomatik hesaplanır (KDV dahil).
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Dönen Varlıklar", deger: h.donen_varliklar, testid: "ozet-donen" },
            { label: "Duran Varlıklar", deger: h.duran_varliklar, testid: "ozet-duran" },
            { label: "Toplam Varlık", deger: h.toplam_varlik, testid: "ozet-varlik" },
            { label: "Kısa Vadeli Borçlar", deger: h.kv_borclar, testid: "ozet-kv" },
            { label: "Toplam Borç", deger: h.toplam_borc, testid: "ozet-borc" },
            { label: "Özkaynak", deger: h.ozkaynak, testid: "ozet-ozkaynak" },
            { label: "Yılbaşından Beri Net Kâr", deger: h.ytd_net_kar, testid: "ozet-netkar" },
            { label: "Yıllıklandırılmış Net Kâr", deger: h.yillik_net_kar, testid: "ozet-yillik-netkar" },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-border/60 p-3" data-testid={s.testid}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
              <p className={`mt-1 font-display text-base font-bold tabular-nums whitespace-nowrap ${s.deger < 0 ? "text-red-700" : ""}`}>
                {formatTL(s.deger)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ==================== SEKME 2: AYLIK BİLANÇO GİRİŞİ ==================== */
const BOS_BILANCO = {
  kasa_banka: "", stok: "", diger_donen: "", duran_varliklar: "",
  saticilar_borcu: "", krediler_kv: "", krediler_uv: "", diger_borclar: "",
};

function BilancoGirisi({ yonetici, bilancolar, onKaydet, yil }) {
  const [form, setForm] = useState({ yil: String(yil), ay: String(new Date().getMonth() + 1), ...BOS_BILANCO });
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const alan = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const sayi = (v) => (v === "" || v === null ? 0 : parseFloat(v) || 0);

  const varlikAlanlari = [
    { key: "kasa_banka", label: "Kasa & Banka" },
    { key: "stok", label: "Stok" },
    { key: "diger_donen", label: "Diğer Dönen Varlıklar" },
    { key: "duran_varliklar", label: "Duran Varlıklar" },
  ];
  const borcAlanlari = [
    { key: "saticilar_borcu", label: "Satıcılara Borçlar" },
    { key: "krediler_kv", label: "Krediler (Kısa Vadeli)" },
    { key: "krediler_uv", label: "Krediler (Uzun Vadeli)" },
    { key: "diger_borclar", label: "Diğer Kısa Vadeli Borçlar" },
  ];

  const varlikToplam = varlikAlanlari.reduce((a, f) => a + sayi(form[f.key]), 0);
  const borcToplam = borcAlanlari.reduce((a, f) => a + sayi(form[f.key]), 0);

  const kaydet = async () => {
    setKaydediliyor(true);
    try {
      const body = { yil: parseInt(form.yil), ay: parseInt(form.ay) };
      [...varlikAlanlari, ...borcAlanlari].forEach((f) => {
        body[f.key] = sayi(form[f.key]);
      });
      const res = await api.post("/bilanco", body);
      toast.success(res.data.guncellendi ? "Bilanço kaydı güncellendi." : "Bilanço kaydı oluşturuldu.");
      onKaydet();
    } catch (err) {
      toast.error(getErrMsg(err, "Bilanço kaydedilemedi."));
    } finally {
      setKaydediliyor(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/bilanco/${id}`);
      toast.success("Bilanço kaydı silindi.");
      onKaydet();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt silinemedi."));
    }
  };

  const duzenle = (b) => {
    setForm({
      yil: String(b.yil),
      ay: String(b.ay),
      kasa_banka: String(b.kasa_banka ?? ""),
      stok: String(b.stok ?? ""),
      diger_donen: String(b.diger_donen ?? ""),
      duran_varliklar: String(b.duran_varliklar ?? ""),
      saticilar_borcu: String(b.saticilar_borcu ?? ""),
      krediler_kv: String(b.krediler_kv ?? ""),
      krediler_uv: String(b.krediler_uv ?? ""),
      diger_borclar: String(b.diger_borclar ?? ""),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-4">
      {!yonetici && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2.5 text-xs text-blue-800" data-testid="bilanco-salt-okunur-uyari">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Bilanço verilerini yalnızca Yönetici rolü girebilir. Kayıtları görüntüleyebilirsiniz.
        </div>
      )}

      {yonetici && (
        <div className="rounded-xl border bg-card p-4" data-testid="bilanco-form">
          <h3 className="font-display text-sm font-semibold">Aylık Bilanço Girişi</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Ticari alacaklar Ciro modülünden otomatik hesaplanır — bu formda girmenize gerek yoktur. Aynı dönem için tekrar kayıt girerseniz mevcut kayıt güncellenir.
          </p>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Yıl</Label>
              <Select value={form.yil} onValueChange={(v) => alan("yil", v)}>
                <SelectTrigger className="min-h-[44px]" data-testid="bilanco-yil-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {["2025", "2026", "2027"].map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ay</Label>
              <Select value={form.ay} onValueChange={(v) => alan("ay", v)}>
                <SelectTrigger className="min-h-[44px]" data-testid="bilanco-ay-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  {AYLAR.map((a, i) => (
                    <SelectItem key={a} value={String(i + 1)} data-testid={`bilanco-ay-${i + 1}`}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border/60 p-3.5">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Varlıklar</p>
              <div className="mt-3 space-y-3">
                {varlikAlanlari.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label} (₺)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      placeholder="0"
                      value={form[f.key]}
                      onChange={(e) => alan(f.key, e.target.value)}
                      className="min-h-[44px] tabular-nums"
                      data-testid={`bilanco-input-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md bg-emerald-50/70 px-3 py-2 text-xs">
                <span className="font-medium text-emerald-800">Girilen Varlık Toplamı</span>
                <span className="font-bold tabular-nums text-emerald-800" data-testid="bilanco-varlik-toplam">{formatTL(varlikToplam)}</span>
              </div>
            </div>

            <div className="rounded-lg border border-border/60 p-3.5">
              <p className="text-xs font-bold uppercase tracking-wide text-red-700">Yükümlülükler (Borçlar)</p>
              <div className="mt-3 space-y-3">
                {borcAlanlari.map((f) => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label} (₺)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      placeholder="0"
                      value={form[f.key]}
                      onChange={(e) => alan(f.key, e.target.value)}
                      className="min-h-[44px] tabular-nums"
                      data-testid={`bilanco-input-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md bg-red-50/70 px-3 py-2 text-xs">
                <span className="font-medium text-red-800">Girilen Borç Toplamı</span>
                <span className="font-bold tabular-nums text-red-800" data-testid="bilanco-borc-toplam">{formatTL(borcToplam)}</span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={kaydet} disabled={kaydediliyor} className="min-h-[44px] gap-1.5" data-testid="bilanco-kaydet-button">
              {kaydediliyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Bilançoyu Kaydet
            </Button>
            <Button variant="outline" onClick={() => setForm((f) => ({ ...f, ...BOS_BILANCO }))} className="min-h-[44px]" data-testid="bilanco-temizle-button">
              Temizle
            </Button>
          </div>
        </div>
      )}

      {/* Kayıt listesi */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="bilanco-liste">
        <h3 className="font-display text-sm font-semibold px-4 pt-4">Kayıtlı Bilançolar</h3>
        {bilancolar.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="bilanco-liste-bos">Henüz bilanço kaydı yok.</p>
        ) : (
          <div className="w-full overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Dönem</th>
                  <th className="px-3 py-2.5 text-right">Kasa & Banka</th>
                  <th className="px-3 py-2.5 text-right">Stok</th>
                  <th className="px-3 py-2.5 text-right">Duran Varlık</th>
                  <th className="px-3 py-2.5 text-right">KV Borçlar</th>
                  <th className="px-3 py-2.5 text-right">UV Krediler</th>
                  {yonetici && <th className="px-3 py-2.5 text-right">İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {bilancolar.map((b) => (
                  <tr key={b.id} className="border-t border-border/60" data-testid={`bilanco-satir-${b.yil}-${b.ay}`}>
                    <td className="px-4 py-2.5 font-medium whitespace-nowrap">{AYLAR[b.ay - 1]} {b.yil}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(b.kasa_banka)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(b.stok)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(b.duran_varliklar)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL((b.saticilar_borcu || 0) + (b.krediler_kv || 0) + (b.diger_borclar || 0))}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(b.krediler_uv)}</td>
                    {yonetici && (
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => duzenle(b)} className="h-8 w-8" data-testid={`bilanco-duzenle-${b.yil}-${b.ay}`} aria-label="Düzenle">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => sil(b.id)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`bilanco-sil-${b.yil}-${b.ay}`} aria-label="Sil">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== SEKME 3: YATIRIM KARAR ARACI ==================== */
function YatirimAraci({ yonetici, senaryolar, onDegisim }) {
  const [ad, setAd] = useState("");
  const [tutar, setTutar] = useState("");
  const [oran, setOran] = useState("25");
  const [akislar, setAkislar] = useState(["", "", ""]);
  const [sonuc, setSonuc] = useState(null);
  const [hesaplaniyor, setHesaplaniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const akisDegistir = (i, v) => setAkislar((a) => a.map((x, j) => (j === i ? v : x)));
  const akisEkle = () => setAkislar((a) => [...a, ""]);
  const akisSil = (i) => setAkislar((a) => a.filter((_, j) => j !== i));

  const govde = () => ({
    yatirim_tutari: parseFloat(tutar) || 0,
    iskonto_orani: parseFloat(oran) || 0,
    nakit_akis_projeksiyonu: akislar.map((a) => parseFloat(a) || 0),
  });

  const gecerliMi = () => {
    if (!(parseFloat(tutar) > 0)) {
      toast.error("Yatırım tutarı 0'dan büyük olmalıdır.");
      return false;
    }
    if (!(parseFloat(oran) > 0 && parseFloat(oran) <= 100)) {
      toast.error("İskonto oranı %0 ile %100 arasında olmalıdır.");
      return false;
    }
    if (akislar.length === 0) {
      toast.error("En az bir yıllık nakit akışı girin.");
      return false;
    }
    return true;
  };

  const hesapla = async () => {
    if (!gecerliMi()) return;
    setHesaplaniyor(true);
    try {
      const res = await api.post("/yatirim-hesapla", govde());
      setSonuc(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Hesaplama yapılamadı."));
    } finally {
      setHesaplaniyor(false);
    }
  };

  const kaydet = async () => {
    if (!ad.trim()) {
      toast.error("Senaryo adı girin.");
      return;
    }
    if (!gecerliMi()) return;
    setKaydediliyor(true);
    try {
      await api.post("/yatirim-senaryolari", { senaryo_adi: ad.trim(), ...govde() });
      toast.success("Senaryo kaydedildi.");
      onDegisim();
    } catch (err) {
      toast.error(getErrMsg(err, "Senaryo kaydedilemedi."));
    } finally {
      setKaydediliyor(false);
    }
  };

  const senaryoSil = async (id) => {
    try {
      await api.delete(`/yatirim-senaryolari/${id}`);
      toast.success("Senaryo silindi.");
      onDegisim();
    } catch (err) {
      toast.error(getErrMsg(err, "Senaryo silinemedi."));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Giriş formu */}
        <div className="rounded-xl border bg-card p-4" data-testid="yatirim-form">
          <h3 className="font-display text-sm font-semibold">Yatırım Senaryosu</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Yatırım tutarı, beklenen yıllık nakit akışları ve iskonto oranı ile NŞD (Net Şimdiki Değer) ve İVO (İç Verim Oranı) hesaplanır.
          </p>

          <div className="mt-4 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Senaryo Adı</Label>
              <Input
                placeholder="Örn: Yeni boya kabini yatırımı"
                value={ad}
                onChange={(e) => setAd(e.target.value)}
                className="min-h-[44px]"
                data-testid="yatirim-ad-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Yatırım Tutarı (₺)</Label>
                <Input
                  type="number" min="0" step="any" inputMode="decimal" placeholder="0"
                  value={tutar} onChange={(e) => setTutar(e.target.value)}
                  className="min-h-[44px] tabular-nums" data-testid="yatirim-tutar-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">İskonto Oranı (%)</Label>
                <Input
                  type="number" min="0" max="100" step="any" inputMode="decimal"
                  value={oran} onChange={(e) => setOran(e.target.value)}
                  className="min-h-[44px] tabular-nums" data-testid="yatirim-oran-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Yıllık Nakit Akışı Projeksiyonu (₺)</Label>
              {akislar.map((a, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-12 shrink-0 text-xs text-muted-foreground tabular-nums">{i + 1}. Yıl</span>
                  <Input
                    type="number" step="any" inputMode="decimal" placeholder="0"
                    value={a} onChange={(e) => akisDegistir(i, e.target.value)}
                    className="min-h-[44px] tabular-nums" data-testid={`yatirim-akis-input-${i + 1}`}
                  />
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => akisSil(i)}
                    disabled={akislar.length <= 1}
                    className="h-9 w-9 shrink-0 text-destructive hover:text-destructive"
                    data-testid={`yatirim-akis-sil-${i + 1}`}
                    aria-label={`${i + 1}. yıl satırını sil`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={akisEkle} disabled={akislar.length >= 30} className="min-h-[40px] gap-1" data-testid="yatirim-akis-ekle-button">
                <Plus className="h-3.5 w-3.5" /> Yıl Ekle
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button onClick={hesapla} disabled={hesaplaniyor} className="min-h-[44px] gap-1.5" data-testid="yatirim-hesapla-button">
                {hesaplaniyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                Hesapla
              </Button>
              {yonetici && (
                <Button variant="outline" onClick={kaydet} disabled={kaydediliyor} className="min-h-[44px] gap-1.5" data-testid="yatirim-kaydet-button">
                  {kaydediliyor ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Senaryoyu Kaydet
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Sonuç paneli */}
        <div className="rounded-xl border bg-card p-4" data-testid="yatirim-sonuc-panel">
          <h3 className="font-display text-sm font-semibold">Karar Sonucu</h3>
          {!sonuc ? (
            <div className="flex h-48 flex-col items-center justify-center text-center" data-testid="yatirim-sonuc-bos">
              <Calculator className="h-8 w-8 text-muted-foreground/40" />
              <p className="mt-2 text-sm text-muted-foreground">Değerleri girip "Hesapla" butonuna basın.</p>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${sonuc.karar === "yapilabilir" ? "border-emerald-200 bg-emerald-50/70" : "border-red-200 bg-red-50/70"}`} data-testid="yatirim-karar">
                {sonuc.karar === "yapilabilir" ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className={`text-sm font-bold ${sonuc.karar === "yapilabilir" ? "text-emerald-700" : "text-red-700"}`}>
                  {sonuc.karar === "yapilabilir" ? "Yatırım Yapılabilir (NŞD > 0)" : "Yatırım Önerilmez (NŞD ≤ 0)"}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-border/60 p-3" data-testid="yatirim-npv">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">NŞD (NPV)</p>
                  <p className={`mt-1 font-display text-base font-bold tabular-nums ${sonuc.npv >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {formatTL(sonuc.npv)}
                  </p>
                </div>
                <div className="rounded-lg border border-border/60 p-3" data-testid="yatirim-irr">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">İVO (IRR)</p>
                  <p className="mt-1 font-display text-base font-bold tabular-nums">
                    {sonuc.irr != null ? `%${sonuc.irr.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}` : "—"}
                  </p>
                </div>
                <div className="rounded-lg border border-border/60 p-3" data-testid="yatirim-geri-odeme">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Geri Ödeme</p>
                  <p className="mt-1 font-display text-base font-bold tabular-nums">
                    {sonuc.geri_odeme_yil != null ? `${sonuc.geri_odeme_yil.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} yıl` : "—"}
                  </p>
                </div>
              </div>

              <div className="w-full overflow-x-auto rounded-lg border border-border/60">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr className="text-left uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2">Yıl</th>
                      <th className="px-3 py-2 text-right">Nakit Akışı</th>
                      <th className="px-3 py-2 text-right">İskontolu Değer</th>
                      <th className="px-3 py-2 text-right">Kümülatif NŞD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sonuc.yillik.map((y) => (
                      <tr key={y.yil} className="border-t border-border/60" data-testid={`yatirim-yil-satir-${y.yil}`}>
                        <td className="px-3 py-2 font-medium">{y.yil}. Yıl</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatTL(y.nakit_akis)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatTL(y.iskontolu)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${y.kumulatif_nsd >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                          {formatTL(y.kumulatif_nsd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Kayıtlı senaryolar */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="yatirim-senaryo-liste">
        <h3 className="font-display text-sm font-semibold px-4 pt-4">Kayıtlı Senaryolar</h3>
        {senaryolar.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground" data-testid="yatirim-senaryo-bos">Henüz kayıtlı senaryo yok.</p>
        ) : (
          <div className="w-full overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Senaryo</th>
                  <th className="px-3 py-2.5 text-right">Yatırım</th>
                  <th className="px-3 py-2.5 text-right">İskonto</th>
                  <th className="px-3 py-2.5 text-right">NŞD</th>
                  <th className="px-3 py-2.5 text-right">İVO</th>
                  <th className="px-3 py-2.5">Karar</th>
                  <th className="px-3 py-2.5">Tarih</th>
                  {yonetici && <th className="px-3 py-2.5 text-right">İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {senaryolar.map((s) => (
                  <tr key={s.id} className="border-t border-border/60" data-testid={`yatirim-senaryo-satir-${s.id}`}>
                    <td className="px-4 py-2.5 font-medium">{s.senaryo_adi}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(s.yatirim_tutari)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">%{s.iskonto_orani.toLocaleString("tr-TR")}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold ${s.npv >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatTL(s.npv)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{s.irr != null ? `%${s.irr.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}` : "—"}</td>
                    <td className="px-3 py-2.5">
                      {s.karar === "yapilabilir" ? (
                        <Badge className={DURUM_STIL.iyi.cls}>Yapılabilir</Badge>
                      ) : (
                        <Badge className={DURUM_STIL.zayif.cls}>Önerilmez</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{formatDate(s.created_at)}</td>
                    {yonetici && (
                      <td className="px-3 py-2.5 text-right">
                        <Button variant="ghost" size="icon" onClick={() => senaryoSil(s.id)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`yatirim-senaryo-sil-${s.id}`} aria-label="Senaryoyu sil">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== SEKME 4: FİNANSAL KAVRAMLAR ==================== */
const KAVRAMLAR = [
  {
    baslik: "Cari Oran",
    formul: "Cari Oran = Dönen Varlıklar ÷ Kısa Vadeli Borçlar",
    aciklama: "Şirketin kısa vadeli borçlarını dönen varlıklarıyla karşılama gücünü gösterir. 1,5 ve üzeri sağlıklı kabul edilir; 1'in altı, kısa vadeli borçların dönen varlıklarla karşılanamadığı anlamına gelir ve likidite riski işaretidir.",
  },
  {
    baslik: "Asit-Test (Likidite) Oranı",
    formul: "Asit-Test = (Dönen Varlıklar − Stok) ÷ Kısa Vadeli Borçlar",
    aciklama: "Stoklar hızlıca nakde çevrilemeyebileceği için hesaptan çıkarılır. 1 ve üzeri, şirketin stok satmadan da kısa vadeli borçlarını ödeyebileceğini gösterir. Servis ağırlıklı işletmelerde bu oran cari orana yakın çıkar.",
  },
  {
    baslik: "Nakit Oran",
    formul: "Nakit Oran = Kasa & Banka ÷ Kısa Vadeli Borçlar",
    aciklama: "En muhafazakâr likidite ölçüsüdür; yalnızca eldeki nakitle borç ödeme gücünü ölçer. 0,2 (yani KV borçların %20'si kadar nakit) genellikle yeterli görülür. Çok yüksek olması da nakdin atıl kaldığına işaret edebilir.",
  },
  {
    baslik: "Kaldıraç Oranı",
    formul: "Kaldıraç = Toplam Borç ÷ Toplam Varlık",
    aciklama: "Varlıkların ne kadarının borçla finanse edildiğini gösterir. 0,5'in altı (varlıkların yarısından azı borçla finanse edilmiş) güvenli bölgedir. 0,7'nin üzeri yüksek borçluluk ve finansal risk anlamına gelir.",
  },
  {
    baslik: "DSO — Alacak Tahsil Süresi",
    formul: "DSO = (Ticari Alacaklar ÷ Dönem Cirosu) × Dönemdeki Gün Sayısı",
    aciklama: "Faturalanan işin ortalama kaç günde tahsil edildiğini gösterir. Sigorta ağırlıklı çalışan servislerde DSO doğal olarak yüksektir; 45 gün altı iyi, 90 gün üzeri nakit akışını zorlayan seviyedir. DSO'yu düşürmek işletme sermayesi ihtiyacını azaltır.",
  },
  {
    baslik: "ROE — Özkaynak Kârlılığı",
    formul: "ROE = Yıllıklandırılmış Net Kâr ÷ Özkaynak × 100",
    aciklama: "Ortakların şirkete koyduğu sermayenin yıllık getiri oranıdır. Türkiye koşullarında enflasyon ve alternatif getiriler (mevduat vb.) dikkate alındığında %20 üzeri güçlü kabul edilir.",
  },
  {
    baslik: "ROA — Varlık Kârlılığı",
    formul: "ROA = Yıllıklandırılmış Net Kâr ÷ Toplam Varlık × 100",
    aciklama: "Şirketin sahip olduğu tüm varlıkları ne kadar verimli kullanarak kâr ürettiğini gösterir. %10 üzeri iyi bir seviyedir. ROE ile birlikte okunmalıdır: ROE yüksek ama ROA düşükse kârlılık borçlanma (kaldıraç) ile sağlanıyor demektir.",
  },
  {
    baslik: "NŞD (NPV) — Net Şimdiki Değer",
    formul: "NŞD = −Yatırım + Σ [ Nakit Akışıₜ ÷ (1 + iskonto oranı)ᵗ ]",
    aciklama: "Gelecekteki nakit akışlarının bugünkü değerinden yatırım tutarının çıkarılmasıyla bulunur. NŞD > 0 ise yatırım, beklenen getiri oranının (iskonto oranı) üzerinde değer yaratıyor demektir ve yapılabilir kabul edilir. İskonto oranı olarak genellikle sermaye maliyeti veya alternatif getiri oranı kullanılır.",
  },
  {
    baslik: "İVO (IRR) — İç Verim Oranı",
    formul: "NŞD'yi sıfır yapan iskonto oranıdır.",
    aciklama: "Yatırımın kendi getiri oranıdır. İVO, kullandığınız iskonto oranından (sermaye maliyetinden) yüksekse yatırım değer yaratır. Örneğin İVO %35, sermaye maliyetiniz %25 ise yatırım avantajlıdır.",
  },
  {
    baslik: "Geri Ödeme Süresi",
    formul: "Kümülatif nakit akışının yatırım tutarına ulaştığı süre (yıl).",
    aciklama: "Yatırılan paranın kaç yılda geri döndüğünü gösterir. Basit ve sezgisel bir ölçüdür ancak paranın zaman değerini dikkate almaz; bu yüzden NŞD ve İVO ile birlikte değerlendirilmelidir.",
  },
];

function FinansalKavramlar() {
  return (
    <div className="rounded-xl border bg-card p-4" data-testid="kavramlar-panel">
      <h3 className="font-display text-sm font-semibold">Finansal Kavramlar Rehberi</h3>
      <p className="mt-0.5 text-[11px] text-muted-foreground mb-2">
        Oran Paneli ve Yatırım Karar Aracı'nda kullanılan kavramların kısa açıklamaları.
      </p>
      <Accordion type="single" collapsible className="w-full">
        {KAVRAMLAR.map((k, i) => (
          <AccordionItem key={k.baslik} value={`k-${i}`} data-testid={`kavram-${i}`}>
            <AccordionTrigger className="text-sm font-medium hover:no-underline">{k.baslik}</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                <p className="rounded-md bg-muted/60 px-3 py-2 font-mono text-xs text-foreground/90">{k.formul}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{k.aciklama}</p>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
